/**
 * Finds the closest ancestor element matching the given selector.
 * Handles both Element nodes (which have native closest) and non-Element nodes.
 *
 * @param node - The starting node (can be Element, Comment, Text, etc.)
 * @param selector - CSS selector to match
 * @returns The closest matching ancestor element, or null if none found
 */
export function findClosestElement(node: Node, selector: string): HTMLElement | null {
  // If the node is already an Element, use native closest
  if (node.nodeType === Node.ELEMENT_NODE) {
    return (node as Element).closest(selector);
  }

  // For non-Element nodes (comment, text, etc.), traverse to find parent Element
  let parent = node.parentNode;

  while (parent) {
    if (parent.nodeType === Node.ELEMENT_NODE) {
      // Found an Element parent, use its closest method
      return (parent as Element).closest(selector);
    }
    parent = parent.parentNode;
  }

  return null;
}

/**
 * Gets the nearest parent Element from a Node.
 * If the node is already an Element, returns it.
 *
 * @param node - The starting node
 * @returns The node if it's an Element, or its nearest Element parent, or null
 */
export function getElementFromNode(node: Node): Element | null {
  if (node.nodeType === Node.ELEMENT_NODE) {
    return node as Element;
  }

  let parent = node.parentNode;
  while (parent) {
    if (parent.nodeType === Node.ELEMENT_NODE) {
      return parent as Element;
    }
    parent = parent.parentNode;
  }

  return null;
}
